import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFrappeGetCall } from 'frappe-react-sdk';
import {
  ArrowLeft,
  Calendar,
  User,
  Award,
  MessageSquare,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  XCircle,
  Clock
} from 'lucide-react';

interface EvaluationDetail {
  criteria: string;
  score: number;
  max_score: number;
  comments: string;
  weight: number;
}

interface CategoryEvaluation {
  id: string;
  category: string;
  score: number;
  max_score: number;
  percentage: number;
  rating: string;
  comments: string;
  details: EvaluationDetail[];
}

interface AssessmentDetail {
  id: string;
  session: {
    name: string;
    title: string;
    description: string;
    session_date: string;
    student_1_name: string;
    student_2_name: string;
  };
  doctor: string;
  doctor_display_name: string;
  creation: string;
  modified: string;
  status: string;
  overall_rating: string;
  evaluator_comments: string;
  scores: {
    total_achieved: number;
    total_possible: number;
    overall_percentage: number;
  };
  categories: CategoryEvaluation[];
}

const ScoreIndicator: React.FC<{ percentage: number; size?: 'sm' | 'md' | 'lg' }> = ({
  percentage,
  size = 'md'
}) => {
  const getColor = () => {
    if (percentage >= 80) return 'text-green-600 bg-green-50 border-green-200';
    if (percentage >= 60) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const sizeClasses = {
    sm: 'w-12 h-12 text-sm',
    md: 'w-16 h-16 text-base',
    lg: 'w-20 h-20 text-lg'
  };

  return (
    <div className={`${sizeClasses[size]} rounded-full border-2 ${getColor()} flex items-center justify-center font-bold`}>
      {percentage.toFixed(0)}%
    </div>
  );
};

const RatingBadge: React.FC<{ rating: string }> = ({ rating }) => {
  const getRatingStyle = () => {
    switch (rating.toLowerCase()) {
      case 'excellent':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'good':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'satisfactory':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'needs improvement':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'unsatisfactory':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (!rating) return null;

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getRatingStyle()}`}>
      {rating}
    </span>
  );
};

const CategoryCard: React.FC<{ category: CategoryEvaluation }> = ({ category }) => {
  const [expanded, setExpanded] = React.useState(false);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{category.category}</h3>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                Score: <span className="font-medium">{category.score}/{category.max_score}</span>
              </div>
              <RatingBadge rating={category.rating} />
            </div>
          </div>
          <ScoreIndicator percentage={category.percentage} />
        </div>

        {category.comments && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Comments</h4>
            <p className="text-sm text-gray-600 bg-gray-50 rounded-md p-3">{category.comments}</p>
          </div>
        )}

        {category.details && category.details.length > 0 && (
          <div>
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              {expanded ? 'Hide' : 'Show'} Detailed Breakdown ({category.details.length} criteria)
              <TrendingUp className={`w-4 h-4 ml-1 transform transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </button>

            {expanded && (
              <div className="mt-4 space-y-3">
                {category.details.map((detail, index) => (
                  <div key={index} className="border border-gray-100 rounded-md p-4 bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <h5 className="font-medium text-gray-900">{detail.criteria}</h5>
                      <div className="text-sm font-medium text-gray-700">
                        {detail.score}/{detail.max_score}
                        {detail.weight && ` (Weight: ${detail.weight})`}
                      </div>
                    </div>
                    {detail.comments && (
                      <p className="text-sm text-gray-600">{detail.comments}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const StatusIcon: React.FC<{ status: string }> = ({ status }) => {
  switch (status.toLowerCase()) {
    case 'completed':
      return <CheckCircle className="w-5 h-5 text-green-600" />;
    case 'draft':
      return <Clock className="w-5 h-5 text-gray-600" />;
    case 'in progress':
      return <AlertCircle className="w-5 h-5 text-blue-600" />;
    default:
      return <XCircle className="w-5 h-5 text-red-600" />;
  }
};

export const AssessmentDetail: React.FC = () => {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const navigate = useNavigate();

  const { data: assessmentData, error, isLoading } = useFrappeGetCall<{
    data: AssessmentDetail;
  }>('surgical_training.api.user_assessments.get_assessment_detail', {
    assessment_id: assessmentId
  });

  const assessment = assessmentData?.data;

  const handleBack = () => {
    navigate('/surgical_training/assessments');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="h-6 bg-gray-200 rounded w-1/2 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !assessment) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={handleBack}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Assessments
          </button>

          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-red-800 mb-2">Assessment Not Found</h2>
            <p className="text-red-600">
              {error?.message || "The requested assessment could not be found or you don't have permission to view it."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Assessments
          </button>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Assessment Details
                </h1>
                <h2 className="text-xl text-gray-700 mb-4">{assessment.session.title}</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center text-gray-600">
                    <Calendar className="w-4 h-4 mr-2" />
                    Session Date: {assessment.session.session_date ?
                      new Date(assessment.session.session_date).toLocaleDateString() :
                      'No date specified'
                    }
                  </div>
                  <div className="flex items-center text-gray-600">
                    <User className="w-4 h-4 mr-2" />
                    Assessed by: {assessment.doctor_display_name}
                  </div>
                  <div className="flex items-center text-gray-600">
                    <StatusIcon status={assessment.status} />
                    <span className="ml-2">Status: {assessment.status}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Clock className="w-4 h-4 mr-2" />
                    Completed: {new Date(assessment.creation).toLocaleString()}
                  </div>
                </div>

                {(assessment.session.student_1_name || assessment.session.student_2_name) && (
                  <div className="mt-4">
                    <span className="text-sm font-medium text-gray-700">Students: </span>
                    <span className="text-sm text-gray-600">
                      {[assessment.session.student_1_name, assessment.session.student_2_name]
                        .filter(Boolean)
                        .join(', ')}
                    </span>
                  </div>
                )}
              </div>

              <div className="ml-6 text-center">
                <ScoreIndicator percentage={assessment.scores.overall_percentage} size="lg" />
                <div className="mt-2 text-sm text-gray-600">
                  Overall Score
                </div>
                <div className="text-xs text-gray-500">
                  {assessment.scores.total_achieved}/{assessment.scores.total_possible} points
                </div>
              </div>
            </div>

            {assessment.session.description && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Session Description</h3>
                <p className="text-sm text-gray-600">{assessment.session.description}</p>
              </div>
            )}
          </div>
        </div>

        {/* Overall Comments */}
        {(assessment.evaluator_comments || assessment.overall_rating) && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <MessageSquare className="w-5 h-5 mr-2" />
              Overall Assessment
            </h3>

            {assessment.overall_rating && (
              <div className="mb-4">
                <span className="text-sm font-medium text-gray-700">Overall Rating: </span>
                <RatingBadge rating={assessment.overall_rating} />
              </div>
            )}

            {assessment.evaluator_comments && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Comments</h4>
                <div className="bg-gray-50 rounded-md p-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{assessment.evaluator_comments}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Category Evaluations */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Award className="w-5 h-5 mr-2" />
            Category Breakdown ({assessment.categories.length} categories)
          </h3>

          {assessment.categories.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
              <Award className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No category evaluations found for this assessment.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {assessment.categories.map((category, index) => (
                <CategoryCard key={category.id || index} category={category} />
              ))}
            </div>
          )}
        </div>

        {/* Summary Statistics */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Assessment Summary</h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                {assessment.categories.length}
              </div>
              <div className="text-sm text-gray-600">Categories Evaluated</div>
            </div>

            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">
                {assessment.categories.filter(c => c.percentage >= 80).length}
              </div>
              <div className="text-sm text-gray-600">Excellent Performance</div>
            </div>

            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">
                {Math.round(
                  assessment.categories.reduce((sum, cat) => sum + cat.percentage, 0) /
                  assessment.categories.length
                )}%
              </div>
              <div className="text-sm text-gray-600">Average Category Score</div>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-500">
            <p>
              Assessment ID: {assessment.id} •
              Last Modified: {new Date(assessment.modified).toLocaleString()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentDetail;